<template>
  <div class="data-display-section" v-if="showData">
    <h3>{{ title }}</h3>
    <div class="data-summary">
      <p><strong>Total Rows:</strong> {{ filteredData.length }}</p>
      <p><strong>Columns:</strong> {{ filteredHeaders.length + 1 }}</p>
      <p v-if="!isExpanded && showExpandButton" class="rows-info">
        <strong>Showing:</strong> {{ displayedData.length }} of {{ filteredData.length }} rows
      </p>
    </div>
    
    <!-- Expand/Collapse Button -->
    <div class="expand-controls" v-if="showExpandButton">
      <button 
        @click="toggleExpand" 
        class="expand-btn"
        :title="isExpanded ? 'Show fewer rows' : 'Show all rows'"
      >
        <span class="expand-icon">{{ isExpanded ? '📝' : '📋' }}</span>
        <span class="expand-text">{{ isExpanded ? 'Show Less' : `Show All (${filteredData.length})` }}</span>
      </button>
    </div>
    
    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th class="row-number">#</th>
            <th class="type-column">Type</th>
            <th 
              v-for="(header, index) in filteredHeaders" 
              :key="index" 
              v-show="isColumnVisibleByName(header)"
              :class="[
                'header-cell',
                {
                  'comparison-column-new': header && header.toString().toLowerCase() === 'previous item no.' && title.includes('New Excel'),
                  'comparison-column-old': header && header.toString().toLowerCase() === 'current item no.' && title.includes('Old Excel')
                }
              ]"
              :title="getColumnTooltip(header)"
            >
              <div class="header-content">
                {{ header }}
                <!-- Show "NEW EXCEL KEY" only for "Previous item no." column in New Excel table -->
                <span v-if="header && header.toString().toLowerCase() === 'previous item no.' && title.includes('New Excel')" class="comparison-indicator comparison-new">
                  📋 NEW EXCEL KEY
                </span>
                <!-- Show "OLD EXCEL KEY" only for "Current item no." column in Old Excel table -->
                <span v-if="header && header.toString().toLowerCase() === 'current item no.' && title.includes('Old Excel')" class="comparison-indicator comparison-old">
                  📁 OLD EXCEL KEY
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="row in displayedData" 
            :key="row.rowIndex"
            :class="['data-row', `row-type-${row.type}`]"
          >
            <td class="row-number">{{ row.rowIndex }}</td>
            <td class="type-cell" :class="`type-${row.type}`">{{ row.type }}</td>
            <td 
              v-for="(header, cellIndex) in filteredHeaders" 
              :key="cellIndex"
              v-show="isColumnVisibleByName(header)"
              :class="[
                'data-cell',
                {
                  'comparison-column-new': header && header.toString().toLowerCase() === 'previous item no.' && title.includes('New Excel'),
                  'comparison-column-old': header && header.toString().toLowerCase() === 'current item no.' && title.includes('Old Excel')
                }
              ]"
            >
              {{ row.data[getOriginalIndex(cellIndex)] || '' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'

export default {
  name: 'DataTable',
  props: {
    title: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      default: () => []
    },
    showData: {
      type: Boolean,
      default: false
    },
    isColumnVisible: {
      type: Function,
      required: true
    },
    isColumnVisibleByName: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    // Expand/collapse state
    const isExpanded = ref(false)

    // Filter out empty rows from data (rows where type is empty/default)
    const filteredData = computed(() => {
      return props.data.filter(row => {
        // Keep rows that have a meaningful type (not empty, not 'default', not null/undefined)
        return row.type && row.type !== 'default' && row.type.trim() !== ''
      })
    })

    // Display limited rows (max 10) when not expanded
    const displayedData = computed(() => {
      const data = filteredData.value
      return isExpanded.value ? data : data.slice(0, 10)
    })

    // Check if expand button should be shown
    const showExpandButton = computed(() => {
      return filteredData.value.length > 10
    })

    // Toggle expand/collapse
    const toggleExpand = () => {
      isExpanded.value = !isExpanded.value
    }

    // Filter out the Type column and empty columns from headers
    const filteredHeaders = computed(() => {
      return props.headers
        .map((header, index) => ({ header, originalIndex: index }))
        .filter(({ header }) => {
          // Filter out Type column and empty/undefined headers
          const normalizedHeader = header && header.toString().toLowerCase()
          return normalizedHeader !== 'type' && header !== null && header !== undefined && header !== ''
        })
        .map(({ header }) => header)
    })

    // Create a mapping from filtered index to original index
    const originalIndexMap = computed(() => {
      const map = []
      let filteredIndex = 0
      
      for (let i = 0; i < props.headers.length; i++) {
        const header = props.headers[i]
        const normalizedHeader = header && header.toString().toLowerCase()
        
        if (normalizedHeader !== 'type' && header !== null && header !== undefined && header !== '') {
          map[filteredIndex] = i
          filteredIndex++
        }
      }
      
      return map
    })

    // Get original index from filtered index
    const getOriginalIndex = (filteredIndex) => {
      return originalIndexMap.value[filteredIndex]
    }

    // Get tooltip for column headers
    const getColumnTooltip = (header) => {
      const normalizedHeader = header && header.toString().toLowerCase()
      if (normalizedHeader === 'previous item no.' && props.title.includes('New Excel')) {
        return '📋 NEW EXCEL COMPARISON KEY: This column from the NEW Excel file is used to match rows with the "Current item no." column from the OLD Excel file during comparison'
      }
      if (normalizedHeader === 'current item no.' && props.title.includes('Old Excel')) {
        return '📁 OLD EXCEL COMPARISON KEY: This column from the OLD Excel file is used to match rows with the "Previous item no." column from the NEW Excel file during comparison'
      }
      return ''
    }

    return {
      isExpanded,
      filteredData,
      displayedData,
      showExpandButton,
      toggleExpand,
      filteredHeaders,
      originalIndexMap,
      getOriginalIndex,
      getColumnTooltip
    }
  }
}
</script>

<style scoped>
.data-display-section {
  flex: 1;
  min-width: 400px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.data-display-section h3 {
  margin-top: 0;
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.data-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #6c757d;
}

.data-summary p {
  margin: 0;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.data-table th,
.data-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
}

.data-table th {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.row-number {
  background: #e9ecef !important;
  font-weight: 600;
  text-align: center;
  min-width: 50px;
}

.type-column {
  background: #f8f9fa;
  font-weight: 600;
  text-align: center;
  min-width: 100px;
  color: #495057;
  font-size: 12px;
}

.type-cell {
  font-weight: 500;
  text-align: center;
  font-size: 12px;
  text-transform: capitalize;
  color: #333;
  min-width: 100px;
}

.header-cell {
  color: #495057;
  font-size: 12px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-row:hover {
  background-color: #f5f5f5;
}

/* Row type colors */
.row-type-item {
  background-color: #E9C1C2 !important;
}

.row-type-itemremark {
  background-color: #CFC6E1 !important;
}

.row-type-section {
  background-color: #FFE5B4 !important;
}

.row-type-subsection {
  background-color: #B2DAC3 !important;
}

.row-type-unit {
  background-color: #F6E1C6 !important;
}

.row-type-itemcategory {
  background-color: #C9D9ED !important;
}

.row-type-default {
  background-color: #fafafa !important;
}

/* Type cell specific colors */
.type-item {
  background-color: #E9C1C2 !important;
  color: #721c24;
}

.type-itemremark {
  background-color: #CFC6E1 !important;
  color: #4a148c;
}

.type-section {
  background-color: #FFE5B4 !important;
  color: #e65100;
}

.type-subsection {
  background-color: #B2DAC3 !important;
  color: #1b5e20;
}

.type-unit {
  background-color: #F6E1C6 !important;
  color: #bf360c;
}

.type-itemcategory {
  background-color: #C9D9ED !important;
  color: #0d47a1;
}

.type-default {
  background-color: #fafafa !important;
  color: #424242;
}

/* Comparison column styling */
.comparison-column-new {
  background-color: #e3f2fd !important;
  border: 3px solid #2196f3 !important;
  position: relative;
  box-shadow: 0 0 8px rgba(33, 150, 243, 0.3) !important;
}

.comparison-column-old {
  background-color: #f3e5f5 !important;
  border: 3px solid #9c27b0 !important;
  position: relative;
  box-shadow: 0 0 8px rgba(156, 39, 176, 0.3) !important;
}

.header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.comparison-indicator {
  font-size: 9px;
  font-weight: bold;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 3px;
  padding: 2px 4px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid currentColor;
}

.comparison-indicator.comparison-new {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  color: #1565c0;
  border-color: #2196f3;
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
}

.comparison-indicator.comparison-old {
  background: linear-gradient(135deg, #f3e5f5, #e1bee7);
  color: #7b1fa2;
  border-color: #9c27b0;
  box-shadow: 0 2px 4px rgba(156, 39, 176, 0.3);
}

.header-cell.comparison-column-new {
  background-color: #bbdefb !important;
  color: #1565c0 !important;
  font-weight: bold;
  animation: glow-blue 2s ease-in-out infinite alternate;
}

.header-cell.comparison-column-old {
  background-color: #e1bee7 !important;
  color: #7b1fa2 !important;
  font-weight: bold;
  animation: glow-purple 2s ease-in-out infinite alternate;
}

.header-cell.comparison-column-new .comparison-indicator {
  color: #1565c0;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-color: #2196f3;
}

.header-cell.comparison-column-old .comparison-indicator {
  color: #7b1fa2;
  background: linear-gradient(135deg, #f3e5f5, #e1bee7);
  border-color: #9c27b0;
}

/* Glow animations */
@keyframes glow-blue {
  from {
    box-shadow: 0 0 8px rgba(33, 150, 243, 0.3);
  }
  to {
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.6);
  }
}

@keyframes glow-purple {
  from {
    box-shadow: 0 0 8px rgba(156, 39, 176, 0.3);
  }
  to {
    box-shadow: 0 0 15px rgba(156, 39, 176, 0.6);
  }
}

/* Expand Controls */
.expand-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.expand-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,123,255,0.2);
}

.expand-btn:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,123,255,0.3);
}

.expand-icon {
  font-size: 16px;
}

.expand-text {
  white-space: nowrap;
}

.rows-info {
  color: #666;
  font-style: italic;
}

/* Responsive Design for DataTable */
@media (max-width: 768px) {
  .data-display-section {
    min-width: auto;
  }
  
  .data-summary {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
